import { useEffect, useMemo, useState } from "react";
import {
  Download, CheckCircle2, FileBox, BookOpen, MessageCircle,
  ShieldCheck, ArrowRight, KeyRound, Copy, Check, Loader2, Package,
  AlertTriangle,
} from "lucide-react";
import { CONFIG, trackEvent, whatsappUrl } from "../config";
import { useI18n } from "../i18n";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const INSIDE = [
  { icon: FileBox, t: "Prop Passer Gold EA (.ex5)", s: "Drop into MT5 → Experts" },
  { icon: KeyRound, t: "DPPG_License.bin", s: "Drop into MT5 → Files" },
  { icon: BookOpen, t: ".set preset + EA User Guide", s: "Setup PDF inside the zip" },
];

const QUICK_START = [
  "We've emailed you the EA + license + setup guide. Save the 4 attachments.",
  "MT5 → File → Open Data Folder → MQL5 → Experts. Drop the .ex5 here.",
  "Go back one folder → open Files. Drop DPPG_License.bin here.",
  "MT5 → Tools → Options → Expert Advisors → tick 'Allow WebRequest for listed URL' → add https://prop.dhokiyas.ae",
  "Restart MT5 → drag the EA onto an XAUUSD M15 chart → Inputs tab → Load the .set preset.",
  "Enable AutoTrading. The EA auto-binds your MT5 account on first run (no input needed).",
];

export default function ThankYou() {
  const { currency, fmtCcy } = useI18n();

  const { paymentToken, paymentId, cfOrderId } = useMemo(() => {
    if (typeof window === "undefined") return { paymentToken: "", paymentId: "", cfOrderId: "" };
    const p = new URLSearchParams(window.location.search);
    return {
      paymentToken: p.get("payment_token") || p.get("token") || "",
      paymentId: p.get("payment_id") || p.get("paymentId") || "",
      cfOrderId: p.get("cf_order_id") || p.get("order_id") || "",
    };
  }, []);

  const [licenseId, setLicenseId] = useState("");
  const [email, setEmail] = useState("");
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [verifiedToken, setVerifiedToken] = useState(paymentToken);

  useEffect(() => {
    document.title = "Payment successful · Get your EA";
    trackEvent("payment_return", {
      currency: currency.code,
      value: currency.price,
      num_items: 1,
      content_ids: ["PPG-EA-V123"],
      content_type: "product",
      content_name: "Dhokiya's Prop Passer Gold EA",
      transaction_id: paymentId || cfOrderId || `ppg-${Date.now()}`,
    });
    window.scrollTo(0, 0);
  }, [currency.code, currency.price, paymentId, cfOrderId]);

  // 1. If no token but we have cf_order_id → poll backend until webhook lands
  useEffect(() => {
    if (verifiedToken || !cfOrderId) return;
    let attempts = 0;
    const id = setInterval(async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API_BASE}/api/payments/cashfree/status/${encodeURIComponent(cfOrderId)}`);
        if (r.ok) {
          const data = await r.json();
          if (data.token) {
            setVerifiedToken(data.token);
            clearInterval(id);
            return;
          }
        }
      } catch {}
      if (attempts >= 15) {
        setVerifying(false);
        setError("We're still waiting for payment confirmation. If you've paid, please refresh in a minute or contact support.");
        clearInterval(id);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [cfOrderId, verifiedToken]);

  // 2. Once we have a verified token → auto-issue license (zero user input)
  useEffect(() => {
    if (!verifiedToken || licenseId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/license/auto-issue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ payment_token: verifiedToken }),
        });
        const data = await r.json();
        if (cancelled) return;
        if (!r.ok) {
          setError(data.detail || "Could not auto-generate license. Please contact support.");
          setVerifying(false);
        } else {
          setLicenseId(data.license_id);
          setEmail(data.email || "");
          setVerifying(false);
          trackEvent("license_issued", { license_id: data.license_id });

          const purchaseKey = `dppg_purchase_tracked_${data.license_id || cfOrderId || paymentId}`;
          if (!localStorage.getItem(purchaseKey)) {
            trackEvent("purchase", {
              currency: data.currency || "INR",
              value: Number(data.amount || 4755),
              content_ids: ["PPG-EA-V123"],
              content_type: "product",
              license_id: data.license_id,
              order_id: cfOrderId || paymentId
            });
            localStorage.setItem(purchaseKey, "1");
          }
          setTimeout(() => document.getElementById("download-section")?.scrollIntoView({ behavior: "smooth" }), 200);
        }
      } catch {
        if (!cancelled) {
          setError("Network error. Please refresh the page.");
          setVerifying(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [verifiedToken, licenseId]);

  const onCopyLicense = async () => {
    try { await navigator.clipboard.writeText(licenseId); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const downloadZip = () => {
    trackEvent("download", { kind: "zip" });
    window.location.href = `${API_BASE}/api/download/zip/${encodeURIComponent(licenseId)}`;
  };
  const downloadLicense = () => {
    window.location.href = `${API_BASE}/api/download/license/${encodeURIComponent(licenseId)}`;
  };
  const downloadEa = () => {
    window.location.href = `${API_BASE}/api/download/ea/${encodeURIComponent(licenseId)}`;
  };

  const isIssued = !!licenseId;

  // Block render if no payment evidence at all
  if (!paymentToken && !cfOrderId && !paymentId) {
    return (
      <div data-testid="thankyou-blocked" className="relative min-h-screen overflow-hidden flex items-center justify-center px-4">
        <div className="max-w-lg panel p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[color:var(--gold)] mx-auto mb-4" />
          <h1 className="font-display font-black text-2xl mb-2">Payment required</h1>
          <p className="text-[color:var(--ink-dim)] text-sm mb-6 leading-relaxed">
            This page is for verified customers. Please complete your payment to access your license.
          </p>
          <a href="/" className="btn-gold inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full text-sm font-display">
            Back to homepage <ArrowRight className="w-4 h-4" />
          </a>
          <div className="mt-4 text-[11px] text-[color:var(--ink-mute)]">
            Already paid but ended up here? <a href={whatsappUrl("Hi, I paid but cannot access /thank-you. My payment ID: ")} target="_blank" rel="noreferrer" className="text-[color:var(--gold)] underline">Contact support on WhatsApp</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="thankyou-page" className="relative min-h-screen overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[760px] h-[760px] gold-ring rounded-full pointer-events-none" />
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(0,255,136,.18), rgba(0,180,90,.06))", border: "1px solid rgba(0,255,136,.35)" }}>
            <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-[color:var(--green)]" />
          </span>
          <div>
            <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[color:var(--green)]">Payment successful</div>
            <div className="font-display font-bold text-base sm:text-lg leading-tight">Your license is being generated…</div>
          </div>
        </div>

        <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight">
          Welcome to <span className="text-[color:var(--gold)] glow-gold">Prop Passer Gold</span>
        </h1>
        <p className="mt-4 sm:mt-5 text-[14px] sm:text-base text-[color:var(--ink-dim)] max-w-2xl leading-relaxed">
          We've sent the EA + license + setup guide to your inbox.
          The EA auto-binds to up to <strong className="text-white">{CONFIG.MAX_ACCOUNTS || 10} MT5 accounts</strong>.
          You don't enter any account numbers anywhere.
        </p>

        {/* Verifying state */}
        {verifying && !isIssued && (
          <div data-testid="verifying-payment" className="mt-8 panel p-6 sm:p-8 flex items-center gap-4">
            <Loader2 className="w-6 h-6 animate-spin text-[color:var(--gold)]" />
            <div>
              <div className="font-display font-bold text-base">Generating your license…</div>
              <div className="text-[12px] text-[color:var(--ink-mute)] mt-1">Cashfree is confirming the transaction. This usually takes 5–15 seconds.</div>
            </div>
          </div>
        )}

        {/* Error / pending state */}
        {!isIssued && !verifying && error && (
          <div data-testid="payment-pending" className="mt-8 panel p-6 sm:p-8 border-amber-500/40">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div className="font-display font-bold text-base">Payment confirmation pending</div>
            </div>
            <p className="text-[13px] text-[color:var(--ink-dim)] leading-relaxed">{error}</p>
            <a href={whatsappUrl(`Hi, I paid but my license isn't unlocking. Order: ${cfOrderId || paymentId || "n/a"}`)} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 text-sm transition-colors">
              <MessageCircle className="w-4 h-4" /> WhatsApp support
            </a>
          </div>
        )}

        {/* License + Download */}
        {isIssued && (
          <div id="download-section" data-testid="download-section" className="mt-8 sm:mt-10 panel p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-[color:var(--green)] opacity-[0.06] blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="chip">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--green)] pulse-dot" />
                  License ready
                </span>
                <span className="chip" style={{ color: "var(--gold)", borderColor: "rgba(231,180,59,.45)" }}>v1.23 · MT5</span>
              </div>
              <div className="font-display font-bold text-base sm:text-lg">Your license &amp; download</div>

              <div className="mt-4 bg-[#0a0a0b] border border-[color:var(--gold)]/30 rounded-lg p-4 relative">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-2">Your license key</div>
                <code data-testid="license-id" className="block text-[14px] sm:text-[16px] text-[color:var(--gold)] break-all font-mono leading-relaxed pr-20 font-bold">
                  {licenseId}
                </code>
                <button onClick={onCopyLicense} data-testid="copy-license-btn"
                  className="absolute top-3 right-3 px-3 py-1.5 rounded-md bg-[#23232a] hover:bg-[#2e2e36] text-[11px] font-mono uppercase tracking-wider text-white inline-flex items-center gap-1.5 transition-colors">
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copied ? "Copied" : "Copy"}
                </button>
              </div>

              <div className="mt-5 grid sm:grid-cols-3 gap-3 sm:gap-4">
                {INSIDE.map(({ icon: Icon, t, s }) => (
                  <div key={t} className="border border-[#23232a] rounded-xl p-4">
                    <div className="w-9 h-9 rounded-lg grid place-items-center border border-[color:var(--gold)]/30 mb-3" style={{ background: "linear-gradient(135deg, rgba(245,208,106,.18), rgba(138,106,21,.05))" }}>
                      <Icon className="w-4 h-4 text-[color:var(--gold)]" />
                    </div>
                    <div className="font-display font-bold text-[13.5px] leading-tight">{t}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--ink-mute)] mt-1.5 leading-snug">{s}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <button onClick={downloadZip} data-testid="download-zip-btn"
                  className="btn-gold w-full h-14 rounded-full inline-flex items-center justify-center gap-2 text-sm sm:text-[15px] font-display">
                  <Package className="w-5 h-5" />Download EA + License (ZIP)<ArrowRight className="w-4 h-4 ml-1" />
                </button>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <button onClick={downloadEa} data-testid="download-ea-btn"
                    className="h-11 rounded-full inline-flex items-center justify-center gap-2 text-[13px] font-display border border-[color:var(--gold)]/45 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 transition-colors">
                    <Download className="w-4 h-4" />Download .ex5 only
                  </button>
                  <button onClick={downloadLicense} data-testid="download-license-btn"
                    className="h-11 rounded-full inline-flex items-center justify-center gap-2 text-[13px] font-display border border-[color:var(--gold)]/45 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 transition-colors">
                    <Download className="w-4 h-4" />Download license.bin only
                  </button>
                </div>
              </div>

              {email && (
                <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-[color:var(--ink-mute)]">
                  <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--gold)]" />
                  License + EA emailed to <span className="text-white">{email}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick start */}
        <div className="mt-6 sm:mt-8 panel p-6 sm:p-7">
          <div className="font-display font-bold text-base sm:text-lg">Quick start (5 minutes)</div>
          <ol className="mt-4 space-y-3 text-[13px] sm:text-sm text-[color:var(--ink-dim)] leading-relaxed list-none">
            {QUICK_START.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-md grid place-items-center font-mono text-[11px] font-bold mt-px" style={{ background: "rgba(231,180,59,.12)", color: "var(--gold)", border: "1px solid rgba(231,180,59,.35)" }}>{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-6 sm:mt-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
          <a href={whatsappUrl("Hi, I just purchased Prop Passer Gold EA and need help with installation.")}
            target="_blank" rel="noreferrer" data-testid="thankyou-whatsapp"
            className="panel p-5 sm:p-6 hover:border-[color:var(--gold)]/40 transition-colors group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(0,255,136,.18), rgba(0,180,90,.05))", border: "1px solid rgba(0,255,136,.35)" }}>
                <MessageCircle className="w-5 h-5 text-[color:var(--green)]" />
              </div>
              <div>
                <div className="font-display font-bold text-[14px]">Need setup help?</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--ink-mute)] mt-0.5">Priority WhatsApp</div>
              </div>
              <ArrowRight className="w-4 h-4 text-[color:var(--ink-mute)] ml-auto group-hover:text-[color:var(--gold)] transition-colors" />
            </div>
          </a>
          <a href="/my-orders" data-testid="thankyou-myorders" className="panel p-5 sm:p-6 hover:border-[color:var(--gold)]/40 transition-colors group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(245,208,106,.18), rgba(138,106,21,.05))", border: "1px solid rgba(231,180,59,.35)" }}>
                <Download className="w-5 h-5 text-[color:var(--gold)]" />
              </div>
              <div>
                <div className="font-display font-bold text-[14px]">Lost your license?</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--ink-mute)] mt-0.5">Re-download anytime at /my-orders</div>
              </div>
              <ArrowRight className="w-4 h-4 text-[color:var(--ink-mute)] ml-auto group-hover:text-[color:var(--gold)] transition-colors" />
            </div>
          </a>
        </div>

        <div className="mt-10 text-center font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">
          {CONFIG.SELLER} · paid {fmtCcy(currency.price)}
        </div>
      </div>
    </div>
  );
}
